// `/pure` opts out of the package's auto-injection of the Stripe.js script at
// import time. Without it, the script loads on every page (including MP-only
// restaurants) and overwrites `window.Stripe`, breaking the v2 fallback path.
import { loadStripe, type Stripe } from '@stripe/stripe-js/pure';

/**
 * Cache Stripe instances per publishable key.
 * Each restaurant may have a different key, so we cache per key
 * to avoid creating multiple instances for the same restaurant
 * while supporting key switches between restaurants.
 */
const stripeCache = new Map<string, Promise<Stripe | null>>();

export function getStripePromise(publishableKey: string): Promise<Stripe | null> {
  if (!publishableKey) return Promise.resolve(null);

  if (!stripeCache.has(publishableKey)) {
    stripeCache.set(publishableKey, loadStripe(publishableKey));
  }
  return stripeCache.get(publishableKey)!;
}

/**
 * Force-load Stripe.js v3 for the MercadoPago fallback 3DS path.
 *
 * The MP form pre-loads Stripe.js v2 (via useStripeV2) for legacy fallback
 * tokenization, which sets window.Stripe to the v2 SDK. When we then call
 * loadStripe() to handle a 3DS challenge, @stripe/stripe-js detects the
 * existing window.Stripe and resolves immediately with the v2 instance —
 * documented behavior, not a bug in the SDK. The downside: v2 has no
 * handleNextAction() so the 3DS challenge throws.
 *
 * This helper clears both the v2 global AND our cached promise so that
 * loadStripe injects the v3 script fresh. v2 dies after this; the caller
 * is expected to gate user retry behind a reload-required UX (v2 can't be
 * resurrected on this page lifetime).
 */
export function forceLoadStripeV3(publishableKey: string): Promise<Stripe | null> {
  delete (window as Window & { Stripe?: unknown }).Stripe;
  stripeCache.delete(publishableKey);
  return getStripePromise(publishableKey);
}
